import React, { useCallback, useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../../Layout/Layout';
import myAxios from '../../AxiosUrl';
import styles from './CallNumbers.module.css';

/**
 * Super Admin → Call Numbers (/superadmin/call-numbers), same layout as the other Super Admin
 * pages (Layout, header card, stat cards, tabs, search bar, table, modals).
 *  - Numbers: the phone numbers (SIP lines). Add / edit / delete, assign to a seller (free with
 *    their paid package — channels follow the package — or paid: 1/2/4 channels, first month's
 *    rent from the seller's call balance), take back. Line state from the calling service.
 *  - Sellers & balances: call balances; add or take money (with a note), balance history.
 *  - Prices & settings: minimum top-up, rent per channel, AI markup, grace days, free channels per package.
 */

const PACKAGE_NAMES = { startup: 'Startup', smallBusiness: 'Small Business', business: 'Business', enterprise: 'Enterprise', custom: 'Custom', trial: 'Trial' };
const TYPE_NAMES = { topup: 'Top-up (bKash)', adjust: 'Adjustment', rent: 'Number rent', call: 'Call', refund: 'Refund' };
const taka = (n) => `৳${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const EMPTY_NUMBER = { number: '', label: '', lineChannels: 1, notes: '', sip: { host: '', port: 5060, username: '', authUsername: '', password: '', callerId: '', outboundProxy: '' } };
const TABS = [
    ['numbers', 'Numbers', 'fa-phone'],
    ['sellers', 'Sellers & Balances', 'fa-wallet'],
    ['usage', 'Usage', 'fa-chart-bar'],
    ['settings', 'Prices & Settings', 'fa-cog'],
];

const Badge = ({ tone, children }) => <span className={`${styles.badge} ${styles[`badge${tone}`]}`}>{children}</span>;

function StatusBadge({ n }) {
    if (n.status === 'in_stock') return <Badge tone="Grey">In stock</Badge>;
    if (n.status === 'suspended') return <Badge tone="Red">Suspended</Badge>;
    if (n.graceUntil) return <Badge tone="Amber">Grace → {fmtDate(n.graceUntil)}</Badge>;
    return <Badge tone="Green">Assigned</Badge>;
}

const LINE = { registered: ['Green', 'Connected'], registering: ['Grey', 'Connecting'], failing: ['Red', 'Failing'] };
function LineBadge({ engine, n }) {
    if (n.status !== 'assigned') return <span className={styles.sub}>—</span>;
    if (!engine?.online) return <span className={styles.sub}>Service offline</span>;
    const l = (engine.lines || []).find((x) => String(x.numberId) === String(n._id));
    if (!l) return <span className={styles.sub}>Loading in a minute</span>;
    const [tone, label] = LINE[l.state] || ['Grey', l.state];
    return (
        <>
            <Badge tone={tone}>{label}</Badge>
            {l.lastError && l.state !== 'registered' ? <div className={styles.sub}>{String(l.lastError).slice(0, 60)}</div> : null}
        </>
    );
}

function Modal({ title, children, actions, onClose }) {
    return (
        <div className={styles.modal} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
                <div className={styles.modalHeader}>
                    <h2>{title}</h2>
                    <button type="button" onClick={onClose} aria-label="Close"><i className="fas fa-times"></i></button>
                </div>
                <div className={styles.modalBody}>{children}</div>
                <div className={styles.modalActions}>{actions}</div>
            </div>
        </div>
    );
}

const Field = ({ label, hint, children }) => (
    <div className={styles.formGroup}>
        <label>{label}</label>
        {children}
        {hint ? <div className={styles.formHint}>{hint}</div> : null}
    </div>
);

function NumberForm({ initial, onSave, onClose, saving }) {
    const [f, setF] = useState(initial);
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
    const setSip = (k) => (e) => setF((x) => ({ ...x, sip: { ...x.sip, [k]: e.target.value } }));
    const editing = !!initial._id;
    return (
        <Modal
            title={editing ? `Edit ${initial.number}` : 'Add Number'}
            onClose={onClose}
            actions={<>
                <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                <button className={styles.submitBtn} onClick={() => onSave(f)} disabled={saving}>
                    {saving ? <><i className={`fas fa-spinner ${styles.spin}`}></i> Saving…</> : <><i className="fas fa-check"></i> Save Number</>}
                </button>
            </>}
        >
            <div className={styles.formGrid}>
                <Field label="Number"><input value={f.number} onChange={set('number')} placeholder="09606123456" /></Field>
                <Field label="Label (optional)"><input value={f.label} onChange={set('label')} /></Field>
                <Field label="Provider channels" hint="Calls at the same time the provider allows on this number (new numbers: 1). Raise it after the provider upgrades the line.">
                    <input type="number" min="1" max="30" value={f.lineChannels ?? 1} onChange={set('lineChannels')} />
                </Field>
                <Field label="SIP host"><input value={f.sip.host} onChange={setSip('host')} placeholder="103.15.140.151" /></Field>
                <Field label="SIP port"><input type="number" value={f.sip.port} onChange={setSip('port')} /></Field>
                <Field label="SIP username"><input value={f.sip.username} onChange={setSip('username')} /></Field>
                <Field label="Auth username (if different)"><input value={f.sip.authUsername} onChange={setSip('authUsername')} /></Field>
                <Field label="SIP password" hint={editing ? (initial.sip?.passwordSet ? 'Saved — leave empty to keep it' : 'Not set') : ''}>
                    <input type="password" autoComplete="new-password" value={f.sip.password} onChange={setSip('password')} />
                </Field>
                <Field label="Caller ID (optional)"><input value={f.sip.callerId} onChange={setSip('callerId')} /></Field>
                <Field label="Outbound proxy (optional)"><input value={f.sip.outboundProxy} onChange={setSip('outboundProxy')} /></Field>
            </div>
            <Field label="Notes"><textarea rows={2} value={f.notes} onChange={set('notes')} /></Field>
        </Modal>
    );
}

function AssignDialog({ number, settings, onClose, onDone }) {
    const [q, setQ] = useState('');
    const [sellers, setSellers] = useState([]);
    const [picked, setPicked] = useState(null);
    const [free, setFree] = useState(true);
    const [channels, setChannels] = useState(1);
    const [busy, setBusy] = useState(false);

    const search = useCallback(async (term) => {
        try {
            const res = await myAxios.get('sa/call/sellers', { params: { q: term } });
            setSellers(res.data?.sellers || []);
        } catch (err) {
            toast.error(errMsg(err, 'Could not search sellers'));
        }
    }, []);
    useEffect(() => {
        const t = setTimeout(() => search(q.trim()), 300);
        return () => clearTimeout(t);
    }, [q, search]);

    const canFree = picked && picked.package?.paid && !picked.hasFree;
    useEffect(() => {
        if (picked) setFree(!!(picked.package?.paid && !picked.hasFree));
    }, [picked]);

    const lineCh = Number(number.lineChannels) || 1;
    const paidOptions = [1, 2, 4].filter((c) => c <= lineCh);
    const rent = (Number(channels) || 0) * (settings?.channelMonthlyBdt || 0);
    const assign = async () => {
        if (!picked) return toast.error('Pick a seller');
        try {
            setBusy(true);
            const res = await myAxios.post(`sa/call/numbers/${number._id}/assign`, { userId: picked._id, free, channels: Number(channels) });
            toast.success(res.data?.message || 'Assigned');
            onDone();
        } catch (err) {
            toast.error(errMsg(err, 'Could not assign'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title={`Assign ${number.number}`}
            onClose={onClose}
            actions={<>
                <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                <button className={styles.submitBtn} onClick={assign} disabled={busy || !picked}>
                    {busy ? <><i className={`fas fa-spinner ${styles.spin}`}></i> Assigning…</> : <><i className="fas fa-user-plus"></i> Assign Number</>}
                </button>
            </>}
        >
            <div className={styles.searchBox}>
                <i className="fas fa-search"></i>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search seller by name, mobile or email..." autoFocus />
            </div>
            <div className={styles.sellerList}>
                {sellers.map((s) => (
                    <button key={s._id} type="button" className={`${styles.sellerItem} ${picked?._id === s._id ? styles.sellerItemActive : ''}`} onClick={() => setPicked(s)}>
                        <span className={styles.strong}>{s.name || '—'}</span> <span className={styles.sub}>{s.mobile}</span>
                        <div className={styles.sub}>
                            {s.package?.paid ? `${PACKAGE_NAMES[s.package.packageType]} (paid) · free number ${s.hasFree ? 'already given' : `available, ${s.package.channels} ch`}` : 'No paid package'}
                            {' · '}balance {taka(s.balance)}{s.numbers.length ? ` · ${s.numbers.length} number${s.numbers.length > 1 ? 's' : ''}` : ''}
                        </div>
                    </button>
                ))}
                {!sellers.length && <div className={styles.sub} style={{ textAlign: 'center', padding: 16 }}>No sellers found.</div>}
            </div>
            {picked && (
                <>
                    <div className={styles.choice}>
                        <button type="button" className={free ? styles.active : ''} onClick={() => canFree && setFree(true)} disabled={!canFree}>
                            Free with package{picked.package?.paid ? ` (${picked.package.channels} ch)` : ''}
                        </button>
                        <button type="button" className={!free ? styles.active : ''} onClick={() => setFree(false)}>Paid number</button>
                    </div>
                    {!canFree && <div className={styles.formHint} style={{ marginBottom: 12 }}>{picked.package?.paid ? 'This seller already has their free number.' : 'No paid package, so no free number.'}</div>}
                    {free && picked.package?.channels > lineCh && (
                        <div className={styles.formHint} style={{ marginBottom: 12 }}>
                            The package gives {picked.package.channels} channels, but the provider allows {lineCh} on this number now — calls run {lineCh} at a time until the provider raises it (then edit Provider channels).
                        </div>
                    )}
                    {!free && (
                        <Field
                            label="Channels"
                            hint={`${taka(rent)} per month — the first month is taken now from the balance (${taka(picked.balance)}).${lineCh < 4 ? ` The provider allows ${lineCh} at once on this number; for more, ask the provider first, then edit Provider channels.` : ''}`}
                        >
                            <select value={channels} onChange={(e) => setChannels(e.target.value)}>
                                {paidOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                    )}
                </>
            )}
        </Modal>
    );
}

function AdjustDialog({ seller, onClose, onDone }) {
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const save = async () => {
        try {
            setBusy(true);
            const res = await myAxios.post(`sa/call/wallet/${seller._id}/adjust`, { amount: Number(amount), note });
            toast.success(`${res.data?.message || 'Saved'} — new balance ${taka(res.data?.balance)}`);
            onDone();
        } catch (err) {
            toast.error(errMsg(err, 'Could not update the balance'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Modal
            title={`Balance: ${seller.name || seller.mobile}`}
            onClose={onClose}
            actions={<>
                <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                <button className={styles.submitBtn} onClick={save} disabled={busy || !amount || !note.trim()}>
                    {busy ? <><i className={`fas fa-spinner ${styles.spin}`}></i> Saving…</> : <><i className="fas fa-check"></i> Update Balance</>}
                </button>
            </>}
        >
            <p className={styles.formHint} style={{ marginTop: 0, marginBottom: 16, fontSize: 14 }}>
                Now <span className={styles.strong}>{taka(seller.balance)}</span>. Enter a plus amount to add (e.g. 500) or a minus amount to take (e.g. -200).
            </p>
            <div className={styles.formGrid}>
                <Field label="Amount (৳)"><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
                <Field label="Note (why)"><input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
            </div>
        </Modal>
    );
}

function HistoryDialog({ seller, onClose }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        myAxios.get(`sa/call/ledger/${seller._id}`).then((r) => setData(r.data)).catch((err) => toast.error(errMsg(err, 'Could not load the history')));
    }, [seller._id]);
    return (
        <Modal title={`History: ${seller.name || seller.mobile}`} onClose={onClose} actions={<button className={styles.cancelBtn} onClick={onClose}>Close</button>}>
            {!data ? (
                <div className={styles.noData}><i className={`fas fa-spinner ${styles.spin}`}></i>Loading...</div>
            ) : !data.rows?.length ? (
                <div className={styles.noData}><i className="fas fa-inbox"></i>No transactions</div>
            ) : (
                <div className={styles.tableContainer} style={{ marginBottom: 0 }}>
                    <table className={`${styles.table} ${styles.tableSmall}`}>
                        <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Balance</th><th>Note / by</th></tr></thead>
                        <tbody>
                            {data.rows.map((l) => (
                                <tr key={l._id}>
                                    <td className={styles.num}>{fmtDateTime(l.createdAt)}</td>
                                    <td>{TYPE_NAMES[l.type] || l.type}</td>
                                    <td className={`${styles.num} ${l.amount >= 0 ? styles.plus : styles.minus}`}>{l.amount >= 0 ? '+' : '−'}{taka(Math.abs(l.amount))}</td>
                                    <td className={styles.num}>{taka(l.balanceAfter)}</td>
                                    <td>{l.note}<div className={styles.sub}>{l.by}</div></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
}

function SettingsTab({ settings, onSaved }) {
    const [f, setF] = useState(settings);
    useEffect(() => setF(settings), [settings]);
    const [busy, setBusy] = useState(false);
    if (!f) return <div className={styles.content}><div className={styles.noData}><i className={`fas fa-spinner ${styles.spin}`}></i>Loading...</div></div>;
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
    const setCh = (k) => (e) => setF((x) => ({ ...x, packageChannels: { ...x.packageChannels, [k]: e.target.value } }));
    const save = async () => {
        try {
            setBusy(true);
            const res = await myAxios.put('sa/call/settings', f);
            toast.success(res.data?.message || 'Saved');
            onSaved(res.data?.settings);
        } catch (err) {
            toast.error(errMsg(err, 'Could not save'));
        } finally {
            setBusy(false);
        }
    };
    const prices = [
        ['minTopupBdt', 'Minimum top-up (৳)'],
        ['channelMonthlyBdt', 'Extra number: rent per channel per month (৳)'],
        ['aiMarkupPct', 'AI calls: markup on top (%) — 0 = no markup'],
        ['linePerMinBdt', 'Phone line per minute (৳) — charged on every AI call'],
        ['softphonePerMinBdt', 'Softphone per minute (৳)'],
        ['usdToBdt', 'USD → BDT'],
        ['graceDays', 'Grace days before suspend'],
    ];
    return (
        <div className={styles.content}>
            <h3 className={styles.sectionTitle}>Prices</h3>
            <div className={styles.formGrid}>
                {prices.map(([k, label]) => (
                    <Field key={k} label={label}><input type="number" value={f[k] ?? ''} onChange={set(k)} /></Field>
                ))}
            </div>
            <h3 className={styles.sectionTitle}>Free number channels by package</h3>
            <div className={styles.formGrid}>
                {['startup', 'smallBusiness', 'business', 'enterprise', 'custom'].map((k) => (
                    <Field key={k} label={PACKAGE_NAMES[k]}><input type="number" min="1" max="30" value={f.packageChannels?.[k] ?? ''} onChange={setCh(k)} /></Field>
                ))}
            </div>
            <div className={styles.saveRow}>
                <button className={styles.submitBtn} onClick={save} disabled={busy}>
                    {busy ? <><i className={`fas fa-spinner ${styles.spin}`}></i> Saving…</> : <><i className="fas fa-save"></i> Save Settings</>}
                </button>
            </div>
        </div>
    );
}

/** Usage: each seller's calls this month, what they paid, the estimated real cost and the margin. */
function UsageTab() {
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const res = await myAxios.get('sa/call/usage', { params: { month } });
                setData(res.data);
            } catch (err) {
                toast.error(errMsg(err, 'Could not load usage'));
            } finally {
                setLoading(false);
            }
        })();
    }, [month]);
    const t = data?.totals || {};
    return (
        <>
            <div className={styles.filtersSection}>
                <div className={styles.filtersRow}>
                    <div className={styles.formGroup} style={{ margin: 0, minWidth: 180 }}>
                        <label>Month</label>
                        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
                    </div>
                    <div className={styles.sub} style={{ flex: 1 }}>
                        Billed = what sellers paid for calls (AI + line + markup; softphone per minute). Real cost = AI (Claude + Soniox) × the dollar rate + the line, from the call records — with no markup the margin is about 0.
                    </div>
                </div>
            </div>
            <div className={styles.statsGrid}>
                {[
                    ['fa-phone-volume', styles.iconGreen, t.calls ?? 0, 'Calls', `${t.minutes ?? 0} minutes`],
                    ['fa-receipt', styles.iconBlue, taka(t.billed), 'Billed for calls', `Real cost ${taka(t.realCost)}`],
                    ['fa-coins', styles.iconAmber, taka(t.margin), 'Margin on calls', `+ number rent ${taka(t.rent)}`],
                    ['fa-wallet', styles.iconGrey, taka(t.topups), 'Top-ups (bKash)', 'paid in this month'],
                ].map(([icon, cls, value, label, meta]) => (
                    <div key={label} className={styles.statCard}>
                        <div className={`${styles.statIcon} ${cls}`}><i className={`fas ${icon}`}></i></div>
                        <div className={styles.statInfo}>
                            <h3>{value}</h3>
                            <p>{label}</p>
                            <span className={styles.statMeta}>{meta}</span>
                        </div>
                    </div>
                ))}
            </div>
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr><th>Seller</th><th>Calls</th><th>By type</th><th>Minutes</th><th>Billed</th><th>Real cost</th><th>Margin</th><th>Rent</th><th>Top-ups</th></tr>
                    </thead>
                    <tbody>
                        {loading && !data ? (
                            <tr><td colSpan="9" className={styles.noData}><i className={`fas fa-spinner ${styles.spin}`}></i>Loading...</td></tr>
                        ) : !data?.sellers?.length ? (
                            <tr><td colSpan="9" className={styles.noData}><i className="fas fa-inbox"></i>No calls this month</td></tr>
                        ) : (
                            data.sellers.map((x) => (
                                <tr key={x._id}>
                                    <td><div className={styles.strong}>{x.name}</div><div className={styles.sub}>{x.mobile}</div></td>
                                    <td className={styles.num}>{x.calls}<div className={styles.sub}>{x.answered} answered</div></td>
                                    <td className={styles.sub} style={{ whiteSpace: 'nowrap' }}>
                                        order {x.byKind.order} · mkt {x.byKind.marketing}<br />support {x.byKind.support} · softphone {x.byKind.softphone}
                                    </td>
                                    <td className={styles.num}>{x.minutes}</td>
                                    <td className={styles.num}>{taka(x.billed)}</td>
                                    <td className={styles.num}>{taka(x.realCost)}</td>
                                    <td className={`${styles.num} ${x.margin >= 0 ? styles.plus : styles.minus}`}>{taka(x.margin)}</td>
                                    <td className={styles.num}>{taka(x.rent)}</td>
                                    <td className={styles.num}>{taka(x.topups)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function CallNumbers() {
    const [tab, setTab] = useState('numbers');
    const [numbers, setNumbers] = useState([]);
    const [counts, setCounts] = useState({});
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [settings, setSettings] = useState(null);
    const [secretKeySet, setSecretKeySet] = useState(true);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [assigning, setAssigning] = useState(null);
    const [sellerQ, setSellerQ] = useState('');
    const [sellers, setSellers] = useState([]);
    const [sellersLoading, setSellersLoading] = useState(false);
    const [adjusting, setAdjusting] = useState(null);
    const [history, setHistory] = useState(null);
    const [engine, setEngine] = useState(null);

    const loadNumbers = useCallback(async () => {
        try {
            setLoading(true);
            const [n, s, e] = await Promise.all([
                myAxios.get('sa/call/numbers', { params: statusFilter ? { status: statusFilter } : {} }),
                myAxios.get('sa/call/settings'),
                myAxios.get('sa/call/engine').catch(() => null),
            ]);
            setEngine(e?.data || { online: false });
            setNumbers(n.data?.numbers || []);
            setCounts(n.data?.counts || {});
            setSecretKeySet(n.data?.secretKeySet !== false);
            setSettings(s.data?.settings || null);
        } catch (err) {
            toast.error(errMsg(err, 'Could not load numbers'));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);
    useEffect(() => { loadNumbers(); }, [loadNumbers]);

    const loadSellers = useCallback(async (term) => {
        try {
            setSellersLoading(true);
            const res = await myAxios.get('sa/call/sellers', { params: { q: term } });
            setSellers(res.data?.sellers || []);
        } catch (err) {
            toast.error(errMsg(err, 'Could not load sellers'));
        } finally {
            setSellersLoading(false);
        }
    }, []);
    useEffect(() => {
        if (tab !== 'sellers') return undefined;
        const t = setTimeout(() => loadSellers(sellerQ.trim()), 300);
        return () => clearTimeout(t);
    }, [tab, sellerQ, loadSellers]);

    const saveNumber = async (f) => {
        try {
            setSaving(true);
            const body = { ...f, lineChannels: Number(f.lineChannels) || 1, sip: { ...f.sip, port: Number(f.sip.port) } };
            const res = f._id ? await myAxios.put(`sa/call/numbers/${f._id}`, body) : await myAxios.post('sa/call/numbers', body);
            toast.success(res.data?.message || 'Saved');
            setForm(null);
            loadNumbers();
        } catch (err) {
            toast.error(errMsg(err, 'Could not save the number'));
        } finally {
            setSaving(false);
        }
    };
    const unassign = async (n) => {
        if (!window.confirm(`Take ${n.number} back from ${n.owner?.name || 'the seller'}? Rent already taken is not refunded.`)) return;
        try {
            const res = await myAxios.post(`sa/call/numbers/${n._id}/unassign`);
            toast.success(res.data?.message || 'Done');
            loadNumbers();
        } catch (err) {
            toast.error(errMsg(err, 'Could not take it back'));
        }
    };
    const remove = async (n) => {
        if (!window.confirm(`Delete ${n.number} from the list?`)) return;
        try {
            const res = await myAxios.delete(`sa/call/numbers/${n._id}`);
            toast.success(res.data?.message || 'Deleted');
            loadNumbers();
        } catch (err) {
            toast.error(errMsg(err, 'Could not delete'));
        }
    };

    const total = (counts.in_stock || 0) + (counts.assigned || 0) + (counts.suspended || 0);
    const lines = engine?.lines || [];
    const connected = lines.filter((l) => l.state === 'registered').length;
    const term = search.trim().toLowerCase();
    const shown = term
        ? numbers.filter((n) => [n.number, n.label, n.owner?.name, n.owner?.mobile].some((v) => String(v || '').toLowerCase().includes(term)))
        : numbers;

    return (
        <Layout>
            <div className={styles.container}>
                <ToastContainer position="top-right" autoClose={4000} />

                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        <h1>Call Numbers</h1>
                        <p>Phone numbers for sellers' AI calling, their call balances and prices</p>
                    </div>
                    <div className={styles.headerActions}>
                        <button className={styles.refreshBtn} onClick={loadNumbers} disabled={loading}>
                            <i className={`fas fa-sync-alt ${loading ? styles.spin : ''}`}></i> Refresh
                        </button>
                        <button className={styles.createBtn} onClick={() => setForm(EMPTY_NUMBER)}>
                            <i className="fas fa-plus"></i> Add Number
                        </button>
                    </div>
                </div>

                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconGreen}`}><i className="fas fa-phone"></i></div>
                        <div className={styles.statInfo}>
                            <h3>{total}</h3>
                            <p>Total Numbers</p>
                            <span className={styles.statMeta}>{counts.in_stock || 0} in stock</span>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconBlue}`}><i className="fas fa-user-check"></i></div>
                        <div className={styles.statInfo}>
                            <h3>{counts.assigned || 0}</h3>
                            <p>Assigned</p>
                            <span className={styles.statMeta}>Working for sellers</span>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconAmber}`}><i className="fas fa-ban"></i></div>
                        <div className={styles.statInfo}>
                            <h3>{counts.suspended || 0}</h3>
                            <p>Suspended</p>
                            <span className={styles.statMeta}>Package or rent unpaid</span>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${engine?.online ? styles.iconGreen : engine ? styles.iconRed : styles.iconGrey}`}><i className="fas fa-signal"></i></div>
                        <div className={styles.statInfo}>
                            <h3>{engine?.online ? `${connected}/${lines.length}` : '—'}</h3>
                            <p>{engine?.online ? 'Lines Connected' : 'Calling Service'}</p>
                            <span className={styles.statMeta}>
                                {!engine ? 'Checking…' : engine.online
                                    ? `${engine.channels?.inUse ?? 0} call(s) now${engine.today ? ` · today ${engine.today.calls} calls, ${taka(engine.today.spendBdt)}` : ''}`
                                    : 'Not running — no calls go out or come in'}
                            </span>
                        </div>
                    </div>
                </div>

                {!secretKeySet && (
                    <div className={styles.noticeError}>
                        <i className="fas fa-exclamation-circle"></i> AICALL_SECRET_KEY is not set on the server, so SIP passwords can't be saved.
                    </div>
                )}

                <div className={styles.tabs}>
                    {TABS.map(([k, label, icon]) => (
                        <button key={k} className={`${styles.tab} ${tab === k ? styles.active : ''}`} onClick={() => setTab(k)}>
                            <i className={`fas ${icon}`}></i> {label}
                        </button>
                    ))}
                </div>

                {tab === 'numbers' && (
                    <>
                        <div className={styles.filtersSection}>
                            <div className={styles.filtersRow}>
                                <div className={styles.searchBox}>
                                    <i className="fas fa-search"></i>
                                    <input type="text" placeholder="Search by number, label or seller..." value={search} onChange={(e) => setSearch(e.target.value)} />
                                </div>
                                <div className={styles.statusFilters}>
                                    {[['', 'All', total], ['in_stock', 'In stock', counts.in_stock], ['assigned', 'Assigned', counts.assigned], ['suspended', 'Suspended', counts.suspended]].map(([k, label, c]) => (
                                        <button key={k || 'all'} className={statusFilter === k ? styles.active : ''} onClick={() => setStatusFilter(k)}>
                                            {label} ({c || 0})
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr><th>Number</th><th>Channels</th><th>Status</th><th>Line</th><th>Seller</th><th>Type / Rent</th><th>Actions</th></tr>
                                </thead>
                                <tbody>
                                    {loading && !numbers.length ? (
                                        <tr><td colSpan="7" className={styles.noData}><i className={`fas fa-spinner ${styles.spin}`}></i>Loading...</td></tr>
                                    ) : shown.length === 0 ? (
                                        <tr><td colSpan="7" className={styles.noData}><i className="fas fa-inbox"></i>{total === 0 ? 'No numbers yet — click Add Number' : 'No numbers found'}</td></tr>
                                    ) : (
                                        shown.map((n) => (
                                            <tr key={n._id}>
                                                <td>
                                                    <div className={`${styles.strong} ${styles.num}`}>{n.number}</div>
                                                    {n.label ? <div className={styles.sub}>{n.label}</div> : null}
                                                    <div className={styles.sub}>{n.sip?.username}@{n.sip?.host}{n.sip?.passwordSet ? '' : ' · no password'}</div>
                                                </td>
                                                <td>
                                                    <div className={`${styles.strong} ${styles.num}`}>{n.status === 'in_stock' ? n.lineChannels : n.activeChannels} at once</div>
                                                    <div className={styles.sub}>
                                                        {n.status === 'in_stock' ? 'provider limit' : `seller ${n.channels} · provider ${n.lineChannels}`}
                                                    </div>
                                                </td>
                                                <td>
                                                    <StatusBadge n={n} />
                                                    {n.suspendedReason ? <div className={styles.sub}>{n.suspendedReason.replace('_', ' ')}</div> : null}
                                                </td>
                                                <td><LineBadge engine={engine} n={n} /></td>
                                                <td>{n.owner ? <><div className={styles.strong}>{n.owner.name || '—'}</div><div className={styles.sub}>{n.owner.mobile}</div></> : <span className={styles.sub}>—</span>}</td>
                                                <td>
                                                    {n.status === 'in_stock' ? <span className={styles.sub}>—</span> : n.free ? <Badge tone="Blue">Free</Badge> : (
                                                        <><div className={styles.num}>{taka(n.monthlyRent)}/mo</div><div className={styles.sub}>until {fmtDate(n.rentPaidUntil)}</div></>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className={styles.actions}>
                                                        <button className={styles.actionBtn} title="Edit" onClick={() => setForm({ ...EMPTY_NUMBER, ...n, sip: { ...EMPTY_NUMBER.sip, ...n.sip, password: '' } })}><i className="fas fa-pen"></i></button>
                                                        {n.status === 'in_stock' && <button className={styles.actionBtn} title="Assign to a seller" onClick={() => setAssigning(n)}><i className="fas fa-user-plus"></i></button>}
                                                        {n.status !== 'in_stock' && <button className={styles.actionDanger} title="Take back" onClick={() => unassign(n)}><i className="fas fa-undo"></i></button>}
                                                        {n.status === 'in_stock' && <button className={styles.actionDanger} title="Delete" onClick={() => remove(n)}><i className="fas fa-trash"></i></button>}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.footer}>
                            <i className="fas fa-info-circle"></i> A free number's channels follow the seller's package and change with it. Paid numbers:{' '}
                            {taka(settings?.channelMonthlyBdt)} per channel per month from the seller's call balance. Package expired or rent unpaid:{' '}
                            {settings?.graceDays ?? 7} days of grace, then suspended.
                        </div>
                    </>
                )}

                {tab === 'sellers' && (
                    <>
                        <div className={styles.filtersSection}>
                            <div className={styles.filtersRow}>
                                <div className={styles.searchBox}>
                                    <i className="fas fa-search"></i>
                                    <input type="text" placeholder="Search seller by name, mobile or email..." value={sellerQ} onChange={(e) => setSellerQ(e.target.value)} />
                                </div>
                            </div>
                        </div>
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead><tr><th>Seller</th><th>Package</th><th>Balance</th><th>Numbers</th><th>Actions</th></tr></thead>
                                <tbody>
                                    {sellersLoading && !sellers.length ? (
                                        <tr><td colSpan="5" className={styles.noData}><i className={`fas fa-spinner ${styles.spin}`}></i>Loading...</td></tr>
                                    ) : sellers.length === 0 ? (
                                        <tr><td colSpan="5" className={styles.noData}><i className="fas fa-inbox"></i>No sellers found</td></tr>
                                    ) : (
                                        sellers.map((s) => (
                                            <tr key={s._id}>
                                                <td><div className={styles.strong}>{s.name || '—'}</div><div className={styles.sub}>{s.mobile}</div></td>
                                                <td>
                                                    {s.package?.paid
                                                        ? <><Badge tone="Green">{PACKAGE_NAMES[s.package.packageType] || s.package.packageType}</Badge><div className={styles.sub}>free number: {s.package.channels} ch</div></>
                                                        : <Badge tone="Grey">No paid package</Badge>}
                                                </td>
                                                <td className={`${styles.num} ${styles.strong}`}>{taka(s.balance)}</td>
                                                <td>
                                                    {s.numbers.length ? s.numbers.map((x) => (
                                                        <div key={x.number} className={styles.num}>
                                                            {x.number}{x.free ? <span className={styles.sub}> (free)</span> : null}{x.status === 'suspended' ? <span className={styles.sub}> · suspended</span> : null}
                                                        </div>
                                                    )) : <span className={styles.sub}>—</span>}
                                                </td>
                                                <td>
                                                    <div className={styles.actions}>
                                                        <button className={styles.actionBtn} title="Add or take balance" onClick={() => setAdjusting(s)}><i className="fas fa-wallet"></i></button>
                                                        <button className={styles.actionBtn} title="Balance history" onClick={() => setHistory(s)}><i className="fas fa-history"></i></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {tab === 'usage' && <UsageTab />}

                {tab === 'settings' && <SettingsTab settings={settings} onSaved={(s) => s && setSettings(s)} />}

                {form && <NumberForm initial={form} saving={saving} onSave={saveNumber} onClose={() => setForm(null)} />}
                {assigning && <AssignDialog number={assigning} settings={settings} onClose={() => setAssigning(null)} onDone={() => { setAssigning(null); loadNumbers(); }} />}
                {adjusting && <AdjustDialog seller={adjusting} onClose={() => setAdjusting(null)} onDone={() => { setAdjusting(null); loadSellers(sellerQ.trim()); }} />}
                {history && <HistoryDialog seller={history} onClose={() => setHistory(null)} />}
            </div>
        </Layout>
    );
}

export default CallNumbers;
